import React from 'react';
import { Play, Pause, RotateCcw, Square } from 'lucide-react';

interface AudioPlayerProps {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  onPlay: () => void;
  onPause: () => void;
  onReplay: () => void;
  onStop: () => void;
}

export function AudioPlayer({
  isPlaying,
  currentTime,
  duration,
  onPlay,
  onPause,
  onReplay,
  onStop
}: AudioPlayerProps) {
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 glass border-b border-gray-800">
      <div className="max-w-lg mx-auto px-4 py-3">
        {/* Progress bar */}
        <div className="h-1 bg-gallery-light rounded-full mb-3 overflow-hidden">
          <div 
            className="h-full bg-gallery-accent transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        
        {/* Controls */}
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-400 w-12">
            {formatTime(currentTime)}
          </span>
          
          <div className="flex items-center gap-4">
            {/* Replay */}
            <button
              onClick={onReplay}
              className="p-2 rounded-full hover:bg-gray-800 transition-colors"
              aria-label="Replay"
            >
              <RotateCcw className="w-5 h-5 text-gallery-light" />
            </button>
            
            {/* Play/Pause */}
            <button
              onClick={isPlaying ? onPause : onPlay}
              className="p-3 rounded-full bg-gallery-accent hover:bg-opacity-90 transition-colors"
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? (
                <Pause className="w-6 h-6 text-gallery-dark" />
              ) : (
                <Play className="w-6 h-6 text-gallery-dark ml-0.5" />
              )}
            </button>
            
            {/* Stop */}
            <button
              onClick={onStop}
              className="p-2 rounded-full hover:bg-gray-800 transition-colors"
              aria-label="Stop"
            >
              <Square className="w-5 h-5 text-gallery-light" />
            </button>
          </div>
          
          <span className="text-xs text-gray-400 w-12 text-right">
            {formatTime(duration)}
          </span>
        </div>
      </div>
    </div>
  );
}
